import React, { useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { InputBase, IconButton, Popover, MenuList, MenuItem, makeStyles } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { updateAccessSelector } from '../../redux/common/roleAccess/selectors';
import BrandingColors from '../../styles/color';

// Modules allowed in the global search scope selector. Names must match the
// menuItemName values returned by user_Role_Order for parent-level items.
const MODULE_WHITELIST = ['Admissions', 'Registration', 'Billing', 'Financial Aid', 'Student Life'];

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        alignItems: 'stretch',
        padding: '8px 12px',
        borderBottom: `1px solid ${BrandingColors.lightGrey}`,
        backgroundColor: BrandingColors.white,
        boxSizing: 'border-box'
    },
    moduleTrigger: {
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 6px 0 10px',
        cursor: 'pointer',
        fontFamily: 'FiraSans-Regular',
        fontSize: '13px',
        color: BrandingColors.blkSideBarFont,
        border: `1px solid ${BrandingColors.lightGrey}`,
        borderRight: 'none',
        borderRadius: '3px 0 0 3px',
        height: '32px',
        flex: '0 0 35%',
        minWidth: 0,
        backgroundColor: BrandingColors.backgroundGrey,
        '& > span:first-child': {
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'left'
        },
        '&:focus-visible': {
            outline: `2px solid ${BrandingColors.textBord}`,
            outlineOffset: '-2px'
        }
    },
    searchWrapper: {
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        minWidth: 0,
        height: '32px',
        border: `1px solid ${BrandingColors.lightGrey}`,
        borderRadius: '0 3px 3px 0',
        paddingLeft: '8px',
        backgroundColor: BrandingColors.white,
        '&:focus-within': {
            borderColor: BrandingColors.textBord,
            boxShadow: `0 0 0 1px ${BrandingColors.textBord}`
        }
    },
    input: {
        flex: 1,
        minWidth: 0,
        fontFamily: 'FiraSans-Regular',
        fontSize: '13px',
        color: BrandingColors.darkGreyFont,
        '& input::placeholder': {
            color: BrandingColors.lightGreyFont,
            opacity: 1
        }
    },
    searchButton: {
        padding: '4px',
        color: BrandingColors.lightGreyFont
    },
    popoverPaper: {
        marginTop: '4px',
        minWidth: '160px',
        border: `1px solid ${BrandingColors.lightGrey}`,
        borderRadius: '3px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.18)'
    },
    menuItem: {
        fontFamily: 'FiraSans-Regular',
        fontSize: '13px',
        color: BrandingColors.blkSideBarFont
    }
}));

// Derives the dropdown options from the user's real permissions. Reads
// parentLevel from Redux, filters by the whitelist and menuAccess !== 0,
// de-duplicates by trimmed name, and prepends "All".
export function deriveModuleOptions(parentLevel) {
    const allowed = new Set();
    (parentLevel || []).forEach((v) => {
        const name = typeof v?.menuItemName === 'string' ? v.menuItemName.trim() : '';
        if (name && v.menuAccess !== 0 && MODULE_WHITELIST.includes(name)) {
            allowed.add(name);
        }
    });
    const ordered = MODULE_WHITELIST.filter((m) => allowed.has(m));
    return ['All', ...ordered];
}

function GlobalSearchBox({ sidebarOpen, query: controlledQuery, onQueryChange, selectedModule: controlledModule, onModuleChange, searchAriaControls, searchAriaExpanded }) {
    const classes = useStyles();
    const { t } = useTranslation();
    const { access } = useSelector(updateAccessSelector) || { access: {} };
    const parentLevel = access?.parentLevel;

    const [internalQuery, setInternalQuery] = useState('');
    const [internalModule, setInternalModule] = useState('All');
    const query = controlledQuery !== undefined ? controlledQuery : internalQuery;
    const selectedModule = controlledModule !== undefined ? controlledModule : internalModule;

    const [anchorEl, setAnchorEl] = useState(null);
    const triggerRef = useRef(null);

    const moduleOptions = useMemo(() => deriveModuleOptions(parentLevel ?? []), [parentLevel]);

    const open = Boolean(anchorEl);

    const handleQueryChange = (value) => {
        if (onQueryChange) onQueryChange(value);
        if (controlledQuery === undefined) setInternalQuery(value);
    };

    const handleOpen = () => setAnchorEl(triggerRef.current);
    const handleClose = () => setAnchorEl(null);
    const handleSelect = (module) => {
        if (onModuleChange) onModuleChange(module);
        if (controlledModule === undefined) setInternalModule(module);
        handleClose();
        triggerRef.current?.focus();
    };
    const handleTriggerKeyDown = (e) => {
        if (e.key === 'Escape' && open) {
            e.preventDefault();
            handleClose();
        }
        if ((e.key === 'Enter' || e.key === ' ') && !open) {
            e.preventDefault();
            handleOpen();
        }
    };

    const labelFor = (m) => (m === 'All' ? t('sidebar.search.moduleAll') : m);

    if (sidebarOpen === false) {
        return null;
    }

    return (
        <div role="search" className={classes.root}>
            <button
                ref={triggerRef}
                type="button"
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls="global-search-module-listbox"
                aria-label={t('sidebar.search.moduleFilterLabel')}
                className={classes.moduleTrigger}
                onClick={handleOpen}
                onKeyDown={handleTriggerKeyDown}
            >
                <span>{labelFor(selectedModule)}</span>
                <ArrowDropDownIcon fontSize="small" />
            </button>
            <div className={classes.searchWrapper}>
                <InputBase
                    className={classes.input}
                    placeholder={t('sidebar.search.placeholder')}
                    inputProps={{
                        role: 'combobox',
                        'aria-label': t('sidebar.search.ariaLabel'),
                        'aria-autocomplete': 'list',
                        'aria-controls': searchAriaControls,
                        'aria-expanded': Boolean(searchAriaExpanded)
                    }}
                    value={query}
                    onChange={(e) => handleQueryChange(e.target.value)}
                />
                <IconButton
                    type="button"
                    className={classes.searchButton}
                    aria-label={t('sidebar.search.submitLabel')}
                    size="small"
                    tabIndex={-1}
                    disabled={!onQueryChange}
                >
                    <SearchIcon fontSize="small" />
                </IconButton>
            </div>
            <Popover
                id="global-search-module-listbox"
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                classes={{ paper: classes.popoverPaper }}
            >
                <MenuList role="listbox" aria-label={t('sidebar.search.moduleFilterLabel')} autoFocus>
                    {moduleOptions.map((m) => (
                        <MenuItem
                            key={m}
                            role="option"
                            aria-selected={selectedModule === m}
                            selected={selectedModule === m}
                            onClick={() => handleSelect(m)}
                            className={classes.menuItem}
                        >
                            {labelFor(m)}
                        </MenuItem>
                    ))}
                </MenuList>
            </Popover>
        </div>
    );
}

GlobalSearchBox.propTypes = {
    sidebarOpen: PropTypes.bool,
    query: PropTypes.string,
    onQueryChange: PropTypes.func,
    selectedModule: PropTypes.string,
    onModuleChange: PropTypes.func,
    searchAriaControls: PropTypes.string,
    searchAriaExpanded: PropTypes.bool
};

GlobalSearchBox.defaultProps = {
    sidebarOpen: true,
    query: undefined,
    onQueryChange: undefined,
    selectedModule: undefined,
    onModuleChange: undefined,
    searchAriaControls: undefined,
    searchAriaExpanded: undefined
};

export default GlobalSearchBox;
